// NÉVE 15 ml cap — max 2 per 15 ml variant, per variant (not per cart).
//
// Three layers, because each entry point behaves differently:
//
//  1. PDP quantity input gets max="3" so the stepper cannot exceed it.
//  2. PDP add-to-cart is intercepted. Dawn sends FormData here, NOT JSON,
//     which is why an earlier JSON-only version never fired on this path.
//     Over-cap adds are clamped; already-at-cap adds are refused with an
//     inline error using Shopify's own error response shape, so Dawn
//     renders it natively in the form.
//  3. Cart page and cart drawer quantity changes are intercepted. Dawn
//     sends JSON with { line, quantity } where `line` is the 1-based
//     position in cart.items, not a variant id. Resolved via /cart.js.
//     This layer identifies 15 ml lines by variant_title, so it works for
//     every product regardless of catalogue size.
//
// LIMITATION: client-side only. Covers every path a real customer can take
// through the theme. Someone calling Shopify's cart API directly could
// still bypass it. Server-enforced caps need either native quantity rules
// (Admin > product > variant > Quantity rules) or a Shopify Function.

(function () {
  'use strict';

  var CAP = 2;

  function is15mlTitle(title) {
    if (!title) return false;
    return /(^|[^\d])15\s*ml/i.test(title);
  }

  function is15mlVariantId(id) {
    var ids = window.__NEVE_15ML_VARIANT_IDS__ || [];
    return ids.indexOf(parseInt(id, 10)) !== -1;
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function qtyInCart(cart, variantId) {
    var vid = parseInt(variantId, 10);
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === vid) return cart.items[i].quantity;
    }
    return 0;
  }

  function toast(msg) {
    var el = document.getElementById('neve-cap-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'neve-cap-toast';
      el.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#1A1A1A;color:#F8F6F3;padding:12px 22px;border-radius:6px;font-family:Manrope,sans-serif;font-size:13px;z-index:99999;box-shadow:0 6px 24px rgba(0,0,0,0.25);opacity:0;transition:opacity .25s ease;max-width:90vw;text-align:center;';
      document.body.appendChild(el);
    }
    el.textContent = msg;
    requestAnimationFrame(function () { el.style.opacity = '1'; });
    clearTimeout(el._t);
    el._t = setTimeout(function () { el.style.opacity = '0'; }, 3600);
  }

  // Shopify's own error shape. Dawn checks `response.status` and pipes
  // `description` into handleErrorMessage(), so this renders inline on the
  // PDP with no extra work from us.
  function refusalResponse() {
    return new Response(JSON.stringify({
      status: 422,
      message: 'Limit reached',
      description: 'Maximum ' + CAP + ' of this 15 ml fragrance can be added per order.'
    }), { status: 200, headers: { 'Content-Type': 'application/json' } });
  }

  var nativeFetch = window.fetch.bind(window);

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isAdd = url.indexOf('/cart/add') !== -1;
    var isChange = url.indexOf('/cart/change') !== -1;

    if (!init || !init.body || (!isAdd && !isChange)) {
      return nativeFetch(input, init);
    }

    // ---- Add to cart: Dawn sends FormData ----
    if (isAdd && typeof FormData !== 'undefined' && init.body instanceof FormData) {
      var fd = init.body;
      var addId = fd.get('id');
      if (!addId || !is15mlVariantId(addId)) return nativeFetch(input, init);

      return getCart().then(function (cart) {
        var requested = parseInt(fd.get('quantity') || '1', 10);
        var allowed = CAP - qtyInCart(cart, addId);

        if (allowed <= 0) return refusalResponse();

        if (requested > allowed) {
          fd.set('quantity', String(allowed));
          toast('Adjusted to ' + allowed + '. Maximum ' + CAP + ' of this 15 ml can be added per order.');
        }
        return nativeFetch(input, init);
      }).catch(function () {
        return nativeFetch(input, init);
      });
    }

    // ---- Quantity change: Dawn sends JSON with { line, quantity } ----
    if (isChange && typeof init.body === 'string') {
      var payload;
      try { payload = JSON.parse(init.body); } catch (e) { return nativeFetch(input, init); }
      if (typeof payload.quantity !== 'number' || payload.quantity <= CAP) {
        return nativeFetch(input, init);
      }

      return getCart().then(function (cart) {
        var item = null;
        if (payload.line) {
          item = cart.items[parseInt(payload.line, 10) - 1] || null;
        } else if (payload.id) {
          var vid = parseInt(payload.id, 10);
          for (var i = 0; i < cart.items.length; i++) {
            if (cart.items[i].variant_id === vid) { item = cart.items[i]; break; }
          }
        }

        if (item && is15mlTitle(item.variant_title)) {
          payload.quantity = CAP;
          toast('Maximum ' + CAP + ' of this 15 ml fragrance can be added per order.');
          return nativeFetch(input, Object.assign({}, init, { body: JSON.stringify(payload) }));
        }
        return nativeFetch(input, init);
      }).catch(function () {
        return nativeFetch(input, init);
      });
    }

    return nativeFetch(input, init);
  };

  // ---- Layer 1: clamp the visible steppers ----
  function clampInputs() {
    // Cart page and drawer rows carry the variant id directly.
    document.querySelectorAll('.quantity__input[data-quantity-variant-id]').forEach(function (input) {
      if (is15mlTitle(input.getAttribute('data-variant-title')) ||
          is15mlVariantId(input.getAttribute('data-quantity-variant-id'))) {
        input.setAttribute('max', String(CAP));
      }
    });

    // Product page: read the currently selected variant from the hidden input.
    document.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
      var hidden = form.querySelector('.product-variant-id, input[name="id"]');
      if (!hidden) return;
      var qty = form.querySelector('.quantity__input');
      if (!qty) return;
      if (is15mlVariantId(hidden.value)) {
        qty.setAttribute('max', String(CAP));
        if (parseInt(qty.value, 10) > CAP) qty.value = String(CAP);
      } else {
        qty.removeAttribute('max');
      }
    });
  }

  function boot() {
    clampInputs();
    new MutationObserver(clampInputs).observe(document.body, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['value']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();